import React, { useState, useEffect } from 'react';
import { Bell, MessageCircle, Mail, Phone, CheckCircle, XCircle, Clock, AlertTriangle, Info, Settings, Trash2 } from 'lucide-react';
import api from '../../services/api';

function SistemaNotificacoes() {
  const [notificacoes, setNotificacoes] = useState([]);
  const [configuracoes, setConfiguracoes] = useState({
    whatsapp: true,
    email: true,
    sininho: true,
    email_vendedor: true,
    email_fernando: true,
    email_admin: true
  });
  const [carregando, setCarregando] = useState(true);
  const [marcandoLida, setMarcandoLida] = useState(false);

  useEffect(() => {
    carregarNotificacoes();
    carregarConfiguracoes();
  }, []);

  const carregarNotificacoes = async () => {
    setCarregando(true);
    try {
      const response = await api.get('/notificacoes');
      setNotificacoes(response.data.data || []);
    } catch (err) {
      console.error('Erro ao carregar notificações:', err);
    } finally {
      setCarregando(false);
    }
  };

  const carregarConfiguracoes = async () => {
    try {
      const response = await api.get('/configuracoes/notificacoes');
      if (response.data.success) {
        setConfiguracoes({ ...configuracoes, ...response.data.data });
      }
    } catch (err) {
      console.error('Erro ao carregar configurações:', err);
    }
  };

  const marcarComoLida = async (id) => {
    setMarcandoLida(id);
    try {
      await api.post(`/notificacoes/${id}/lida`);
      setNotificacoes(notificacoes.map(n =>
        n.id === id ? { ...n, lida: true } : n
      ));
    } catch (err) {
      console.error('Erro ao marcar como lida:', err);
    } finally {
      setMarcandoLida(null);
    }
  };

  const marcarTodasComoLida = async () => {
    const naoLidas = notificacoes.filter(n => !n.lida);
    if (naoLidas.length === 0) return;

    setMarcandoLida('all');
    try {
      await api.post('/notificacoes/marcar-todas-lida');
      setNotificacoes(notificacoes.map(n => ({ ...n, lida: true })));
    } catch (err) {
      console.error('Erro ao marcar todas como lidas:', err);
    } finally {
      setMarcandoLida(null);
    }
  };

  const atualizarConfiguracao = async (chave, valor) => {
    try {
      setConfiguracoes({ ...configuracoes, [chave]: valor });
      await api.post('/configuracoes/notificacoes', { [chave]: valor });
    } catch (err) {
      console.error('Erro ao atualizar configuração:', err);
      // Reverte em caso de erro
      setConfiguracoes({ ...configuracoes, [chave]: !valor });
    }
  };

  const getIconeTipo = (tipo) => {
    switch (tipo) {
      case 'agendamento_sdr':
        return <Calendar size={20} className="text-blue-600" />;
      case 'whatsapp':
        return <MessageCircle size={20} className="text-green-600" />;
      case 'email':
        return <Mail size={20} className="text-purple-600" />;
      case 'telefone':
        return <Phone size={20} className="text-orange-600" />;
      case 'alerta':
        return <AlertTriangle size={20} className="text-red-600" />;
      default:
        return <Bell size={20} className="text-gray-600" />;
    }
  };

  const getCorTipo = (tipo) => {
    switch (tipo) {
      case 'agendamento_sdr':
        return 'bg-blue-50 border-blue-200';
      case 'whatsapp':
        return 'bg-green-50 border-green-200';
      case 'email':
        return 'bg-purple-50 border-purple-200';
      case 'telefone':
        return 'bg-orange-50 border-orange-200';
      case 'alerta':
        return 'bg-red-50 border-red-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const naoLidas = notificacoes.filter(n => !n.lida).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="relative">
            <Bell size={28} className="text-gray-700" />
            {naoLidas > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
                {naoLidas}
              </span>
            )}
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-800">Sistema de Notificações</h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Gerencie notificações de agendamentos do SDR
            </p>
          </div>
        </div>
        <div className="flex gap-3">
          <button
            onClick={marcarTodasComoLida}
            disabled={naoLidas === 0 || marcandoLida === 'all'}
            className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50"
          >
            {marcandoLida === 'all' ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-700"></div>
                Processando...
              </>
            ) : (
              <>
                <CheckCircle size={16} />
                Marcar todas como lidas
              </>
            )}
          </button>
          <button onClick={carregarNotificacoes} className="flex items-center gap-2 px-4 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50">
            <Bell size={16} /> Atualizar
          </button>
        </div>
      </div>

      {/* Configurações Rápidas */}
      <div className="bg-white rounded-xl shadow p-5">
        <h2 className="font-semibold text-gray-700 text-lg mb-4 flex items-center gap-2">
          <Settings size={18} /> Configurações Rápidas
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <MessageCircle size={18} className="text-green-600" />
              <span className="text-sm font-medium text-gray-700">WhatsApp</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={configuracoes.whatsapp}
                onChange={(e) => atualizarConfiguracao('whatsapp', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Mail size={18} className="text-purple-600" />
              <span className="text-sm font-medium text-gray-700">E-mail</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={configuracoes.email}
                onChange={(e) => atualizarConfiguracao('email', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>

          <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
            <div className="flex items-center gap-2">
              <Bell size={18} className="text-blue-600" />
              <span className="text-sm font-medium text-gray-700">Sininho CRM</span>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={configuracoes.sininho}
                onChange={(e) => atualizarConfiguracao('sininho', e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary"></div>
            </label>
          </div>
        </div>
      </div>

      {/* Lista de Notificações */}
      <div className="bg-white rounded-xl shadow">
        <div className="px-5 py-4 border-b border-gray-200">
          <h2 className="font-semibold text-gray-700 text-lg">Histórico de Notificações</h2>
        </div>

        {carregando ? (
          <div className="flex items-center justify-center py-12">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : notificacoes.length === 0 ? (
          <div className="py-12 text-center text-gray-300">
            <Bell size={48} className="mx-auto mb-2 opacity-40" />
            <p className="text-sm">Nenhuma notificação encontrada</p>
          </div>
        ) : (
          <div className="divide-y divide-gray-50">
            {notificacoes.map(notificacao => (
              <div
                key={notificacao.id}
                className={`p-5 hover:bg-gray-50 transition-colors ${!notificacao.lida ? 'bg-blue-50' : ''}`}
              >
                <div className="flex items-start gap-4">
                  <div className={`p-2 rounded-lg ${getCorTipo(notificacao.tipo)}`}>
                    {getIconeTipo(notificacao.tipo)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <h3 className="text-sm font-semibold text-gray-800">
                            {notificacao.titulo}
                          </h3>
                          {!notificacao.lida && (
                            <span className="bg-blue-100 text-blue-600 text-xs px-2 py-0.5 rounded-full">
                              Nova
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 mb-2">{notificacao.mensagem}</p>
                        <div className="flex items-center gap-4 text-xs text-gray-500">
                          <span>{new Date(notificacao.data_criacao).toLocaleString('pt-BR')}</span>
                          <span>{notificacao.tipo}</span>
                          {notificacao.link && (
                            <a href={notificacao.link} className="text-primary hover:underline">
                              Ver detalhes
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 ml-4">
                        {!notificacao.lida && (
                          <button
                            onClick={() => marcarComoLida(notificacao.id)}
                            disabled={marcandoLida === notificacao.id}
                            className="p-1.5 text-gray-400 hover:text-gray-600 transition-colors"
                          >
                            {marcandoLida === notificacao.id ? (
                              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-gray-600"></div>
                            ) : (
                              <CheckCircle size={16} />
                            )}
                          </button>
                        )}
                        <button
                          onClick={() => {
                            setNotificacoes(notificacoes.filter(n => n.id !== notificacao.id));
                          }}
                          className="p-1.5 text-gray-400 hover:text-red-500 transition-colors"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default SistemaNotificacoes;